import { useState, useEffect } from 'react';
import { 
  Search, Clock, Play, AlertTriangle, CheckCircle, XCircle,
  ArrowRight, RefreshCw, Loader2, Filter, FileSearch, Zap
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { toast } from 'sonner';

import { API_URL } from '../../../utils/apiConfig';

export function PilotOpsTab({ token, t }) {
  const [loading, setLoading] = useState(false);
  const [pilotStatus, setPilotStatus] = useState(null);
  const [pilotStats, setPilotStats] = useState(null);
  const [searchResults, setSearchResults] = useState(null);
  const [timeline, setTimeline] = useState(null);
  const [timelineEntityId, setTimelineEntityId] = useState('');
  
  // Search filters
  const [searchAuthId, setSearchAuthId] = useState('');
  const [searchRefId, setSearchRefId] = useState('');
  const [searchSenderEmail, setSearchSenderEmail] = useState('');
  const [searchMerchantEmail, setSearchMerchantEmail] = useState('');
  
  // Dialogs
  const [markPaidDialog, setMarkPaidDialog] = useState(false);
  const [forceExpireDialog, setForceExpireDialog] = useState(false);
  const [selectedTransferId, setSelectedTransferId] = useState('');
  const [selectedAuthId, setSelectedAuthId] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    if (token) {
      fetchPilotStatus();
      fetchPilotStats();
    }
  }, [token]);

  const fetchPilotStatus = async () => {
    try {
      const response = await fetch(`${API_URL}/api/pilot-ops/status`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setPilotStatus(data);
      }
    } catch (error) {
      console.error('Failed to fetch pilot status:', error);
    }
  };

  const fetchPilotStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/pilot-ops/stats`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setPilotStats(data);
      }
    } catch (error) {
      console.error('Failed to fetch pilot stats:', error);
    }
  };

  const handleSearch = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchAuthId) params.append('authorization_id', searchAuthId);
      if (searchRefId) params.append('reference_id', searchRefId);
      if (searchSenderEmail) params.append('sender_email', searchSenderEmail);
      if (searchMerchantEmail) params.append('merchant_email', searchMerchantEmail);
      
      const response = await fetch(`${API_URL}/api/pilot-ops/search?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setSearchResults(data);
        toast.success(`Found ${data.authorizations?.length || 0} authorizations`);
      }
    } catch (error) {
      toast.error('Search failed');
    } finally {
      setLoading(false);
    }
  };

  const handleFetchTimeline = async () => {
    if (!timelineEntityId.trim()) {
      toast.error('Enter an authorization or transaction ID');
      return;
    }
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/pilot-ops/timeline/${timelineEntityId}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setTimeline(data);
      }
    } catch (error) {
      toast.error('Failed to fetch timeline');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkPaid = async () => {
    if (!selectedTransferId.trim()) {
      toast.error('Enter a transfer/payout ID');
      return;
    }
    setActionLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/pilot-ops/transfers/${selectedTransferId}/mark-paid`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        setMarkPaidDialog(false);
        setSelectedTransferId('');
        fetchPilotStats();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to mark as paid');
      }
    } catch (error) {
      toast.error('Operation failed');
    } finally {
      setActionLoading(false);
    }
  };

  const handleForceExpire = async () => {
    if (!selectedAuthId.trim()) {
      toast.error('Enter an authorization ID');
      return;
    }
    setActionLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/pilot-ops/authorizations/${selectedAuthId}/force-expire`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        setForceExpireDialog(false);
        setSelectedAuthId('');
        fetchPilotStats();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to force expire');
      }
    } catch (error) {
      toast.error('Operation failed');
    } finally {
      setActionLoading(false);
    }
  };

  const formatTimestamp = (ts) => {
    if (!ts) return '-';
    return new Date(ts).toLocaleString();
  };

  const getEventTypeColor = (type) => {
    if (type?.includes('created')) return 'text-emerald-600 bg-emerald-50';
    if (type?.includes('authorized')) return 'text-blue-600 bg-blue-50';
    if (type?.includes('captured')) return 'text-green-600 bg-green-50';
    if (type?.includes('redemption')) return 'text-purple-600 bg-purple-50';
    if (type?.includes('expired') || type?.includes('failed')) return 'text-red-600 bg-red-50';
    if (type?.includes('paid') || type?.includes('completed')) return 'text-emerald-600 bg-emerald-50';
    return 'text-slate-600 bg-slate-50';
  };

  return (
    <div className="space-y-6">
      {/* Pilot Mode Banner */}
      {pilotStatus?.pilot_mode && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
            <Zap className="w-5 h-5 text-amber-600" />
          </div>
          <div>
            <h3 className="font-semibold text-amber-800">🧪 SIMULATED (PILOT MODE)</h3>
            <p className="text-sm text-amber-700">
              Provider: <span className="font-mono font-medium">{pilotStatus?.payment_provider}</span> • 
              All transactions are simulated for testing purposes
            </p>
          </div>
        </div>
      )}

      {/* Pilot Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="text-sm text-slate-500 mb-1">Authorizations</div>
          <div className="text-2xl font-bold text-slate-900">{pilotStats?.authorizations?.total || 0}</div>
          <div className="text-xs text-emerald-600 mt-1">
            {pilotStats?.authorizations?.pending_redemption || 0} pending
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="text-sm text-slate-500 mb-1">Redemptions</div>
          <div className="text-2xl font-bold text-slate-900">{pilotStats?.redemptions?.total || 0}</div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="text-sm text-slate-500 mb-1">Payouts</div>
          <div className="text-2xl font-bold text-slate-900">{pilotStats?.payouts?.total || 0}</div>
          <div className="text-xs text-amber-600 mt-1">
            {pilotStats?.payouts?.simulated_payments || 0} simulated
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="text-sm text-slate-500 mb-1">Mock Events</div>
          <div className="text-2xl font-bold text-slate-900">{pilotStats?.events?.mock_events || 0}</div>
        </div>
      </div>

      {/* Search Section */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <Search className="w-5 h-5" />
          Search Pilot Data
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          <div>
            <Label className="text-xs text-slate-500">Authorization ID</Label>
            <Input
              value={searchAuthId}
              onChange={(e) => setSearchAuthId(e.target.value)}
              placeholder="AUTH-..."
              className="mt-1"
            />
          </div>
          <div>
            <Label className="text-xs text-slate-500">Reference ID</Label>
            <Input
              value={searchRefId}
              onChange={(e) => setSearchRefId(e.target.value)}
              placeholder="REF-..."
              className="mt-1"
            />
          </div>
          <div>
            <Label className="text-xs text-slate-500">Sender Email</Label>
            <Input
              value={searchSenderEmail}
              onChange={(e) => setSearchSenderEmail(e.target.value)}
              placeholder="sender@email.com"
              className="mt-1"
            />
          </div>
          <div>
            <Label className="text-xs text-slate-500">Merchant Email</Label>
            <Input
              value={searchMerchantEmail}
              onChange={(e) => setSearchMerchantEmail(e.target.value)}
              placeholder="merchant@email.com"
              className="mt-1"
            />
          </div>
        </div>
        <Button onClick={handleSearch} disabled={loading} data-testid="pilot-search-btn">
          {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
          Search
        </Button>

        {/* Search Results */}
        {searchResults && (
          <div className="mt-6 space-y-4">
            <h4 className="font-medium text-slate-700">
              Results: {searchResults.authorizations?.length || 0} authorizations
            </h4>
            {searchResults.authorizations?.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-2 px-3 text-slate-500 font-medium">Reference</th>
                      <th className="text-left py-2 px-3 text-slate-500 font-medium">Status</th>
                      <th className="text-left py-2 px-3 text-slate-500 font-medium">USD</th>
                      <th className="text-left py-2 px-3 text-slate-500 font-medium">DOP Remaining</th>
                      <th className="text-left py-2 px-3 text-slate-500 font-medium">Created</th>
                      <th className="text-left py-2 px-3 text-slate-500 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {searchResults.authorizations.map((auth) => (
                      <tr key={auth.id} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="py-2 px-3 font-mono text-xs">{auth.reference_id}</td>
                        <td className="py-2 px-3">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            auth.status === 'fully_redeemed' ? 'bg-emerald-100 text-emerald-700' :
                            auth.status?.includes('expired') ? 'bg-red-100 text-red-700' :
                            'bg-amber-100 text-amber-700'
                          }`}>
                            {auth.status}
                          </span>
                        </td>
                        <td className="py-2 px-3">${auth.usd_amount}</td>
                        <td className="py-2 px-3">{auth.dop_remaining_amount?.toLocaleString()} DOP</td>
                        <td className="py-2 px-3 text-xs text-slate-500">{formatTimestamp(auth.created_at)}</td>
                        <td className="py-2 px-3">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => {
                              setTimelineEntityId(auth.id);
                              handleFetchTimeline();
                            }}
                          >
                            <Clock className="w-3 h-3 mr-1" />
                            Timeline
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Pilot Controls */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Mark Transfer Paid */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-emerald-600" />
            Mark Transfer as PAID (Simulated)
          </h3>
          <p className="text-sm text-slate-500 mb-4">
            Simulate completing a payout for testing. Only works with mock provider.
          </p>
          <Button 
            onClick={() => setMarkPaidDialog(true)}
            variant="outline"
            className="border-emerald-200 text-emerald-700 hover:bg-emerald-50"
            data-testid="mark-paid-btn"
          >
            <Play className="w-4 h-4 mr-2" />
            Mark Transfer Paid
          </Button>
        </div>

        {/* Force Expire Authorization */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <XCircle className="w-5 h-5 text-red-600" />
            Force Expire Authorization
          </h3>
          <p className="text-sm text-slate-500 mb-4">
            Immediately expire an authorization for testing edge cases.
          </p>
          <Button 
            onClick={() => setForceExpireDialog(true)}
            variant="outline"
            className="border-red-200 text-red-700 hover:bg-red-50"
            data-testid="force-expire-btn"
          >
            <AlertTriangle className="w-4 h-4 mr-2" />
            Force Expire
          </Button>
        </div>
      </div>

      {/* Timeline Section */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <Clock className="w-5 h-5" />
          Event Timeline
        </h3>
        <div className="flex gap-3 mb-4">
          <Input
            value={timelineEntityId}
            onChange={(e) => setTimelineEntityId(e.target.value)}
            placeholder="Authorization or Transaction ID..."
            className="max-w-md"
          />
          <Button onClick={handleFetchTimeline} disabled={loading} data-testid="fetch-timeline-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSearch className="w-4 h-4" />}
          </Button>
        </div>

        {timeline && (
          <div className="space-y-3">
            <div className="text-sm text-slate-500 mb-2">
              {timeline.event_count} events for <span className="font-mono">{timeline.entity_id}</span>
            </div>
            <div className="relative pl-6 border-l-2 border-slate-200 space-y-4">
              {timeline.timeline?.map((event, idx) => (
                <div key={idx} className="relative">
                  <div className="absolute -left-[29px] w-4 h-4 rounded-full bg-white border-2 border-slate-300"></div>
                  <div className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${getEventTypeColor(event.event_type)}`}>
                    {event.event_type}
                  </div>
                  <div className="text-xs text-slate-500 mt-1">{formatTimestamp(event.timestamp)}</div>
                  {event.provider && (
                    <div className="text-xs text-slate-400">Provider: {event.provider}</div>
                  )}
                  {event.data && Object.keys(event.data).length > 0 && (
                    <pre className="text-xs bg-slate-50 p-2 rounded mt-2 overflow-x-auto max-w-xl">
                      {JSON.stringify(event.data, null, 2)}
                    </pre>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Mark Paid Dialog */}
      <Dialog open={markPaidDialog} onOpenChange={setMarkPaidDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Mark Transfer as PAID (Simulated)</DialogTitle>
            <DialogDescription>
              This will mark the transfer/payout as completed in simulated mode.
              This action is audit-logged.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Transfer/Payout ID</Label>
              <Input
                value={selectedTransferId}
                onChange={(e) => setSelectedTransferId(e.target.value)}
                placeholder="PAY-... or tr_..."
                className="mt-1"
              />
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-sm text-amber-700">
                <AlertTriangle className="w-4 h-4 inline mr-1" />
                This marks the payout as completed without actual bank transfer.
                Only for pilot testing.
              </p>
            </div>
          </div>
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setMarkPaidDialog(false)}>Cancel</Button>
            <Button onClick={handleMarkPaid} disabled={actionLoading} className="bg-emerald-600 hover:bg-emerald-700">
              {actionLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
              Mark as PAID
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Force Expire Dialog */}
      <Dialog open={forceExpireDialog} onOpenChange={setForceExpireDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Force Expire Authorization</DialogTitle>
            <DialogDescription>
              This will immediately expire the authorization and deactivate its redemption tokens.
              This action is audit-logged.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Authorization ID or Reference ID</Label>
              <Input
                value={selectedAuthId}
                onChange={(e) => setSelectedAuthId(e.target.value)}
                placeholder="AUTH-... or reference ID"
                className="mt-1"
              />
            </div>
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-sm text-red-700">
                <AlertTriangle className="w-4 h-4 inline mr-1" />
                This will set the authorization to expired status and prevent any further redemptions.
              </p>
            </div>
          </div>
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setForceExpireDialog(false)}>Cancel</Button>
            <Button onClick={handleForceExpire} disabled={actionLoading} variant="destructive">
              {actionLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <XCircle className="w-4 h-4 mr-2" />}
              Force Expire
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default PilotOpsTab;
